/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * The draggable, resizable widget canvas.
 *
 * ## The app-scale problem, and why this does not use WidthProvider
 *
 * assets/theme/base/appScale.js puts `zoom: 0.8` on <body> above 992px. Under
 * CSS zoom, an element's offsetWidth stays in unzoomed CSS pixels while mouse
 * events arrive in zoomed viewport pixels. react-grid-layout computes a drag
 * from the pointer delta and compares it against column widths derived from
 * the measured width, so the two disagree by the zoom factor: a tile follows
 * the cursor at 80% of its speed and lands a column early.
 *
 * Two corrections, and both are needed:
 *
 *  - Width is measured with offsetWidth through a ResizeObserver rather than
 *    by WidthProvider. WidthProvider measures on window resize only, which
 *    misses the sidenav collapsing — a layout change that does not resize the
 *    window but does halve the grid.
 *
 *  - `transformScale` is handed the live zoom from getCssZoom(). That is the
 *    prop RGL provides for a scaled parent; it divides pointer deltas by the
 *    factor. Re-read on resize because ZoomControls in the navbar lets the
 *    user change the zoom at runtime, and a stale factor is the same bug.
 *
 * Note this does not change how the app is scaled. appScale.js explains why it
 * must stay `zoom` and not `transform: scale()` — transform would establish a
 * containing block and break the fixed Sidenav and every modal portal.
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Responsive as ResponsiveGridLayout } from 'react-grid-layout';
import { Box } from '@mui/material';

import { getCssZoom } from 'assets/theme/base/appScale';

import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

/** Columns per breakpoint. Twelve on wide screens, one on a phone. */
export const BREAKPOINTS = { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 };
export const COLUMNS = { lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 };
export const ROW_HEIGHT = 56;
export const MARGIN = [16, 16];

/** Sensible starting size and minimum per widget kind, in grid units. */
export const WIDGET_SIZES = {
  kpi: { w: 3, h: 2, minW: 2, minH: 2 },
  report_chart: { w: 6, h: 5, minW: 3, minH: 3 },
  list: { w: 6, h: 5, minW: 3, minH: 3 },
  text: { w: 12, h: 2, minW: 2, minH: 1 },
  greeting: { w: 12, h: 2, minW: 3, minH: 2 },
  tasks: { w: 6, h: 5, minW: 3, minH: 3 },
};

export default function DashboardGrid({ layout, widgets, editable, onLayoutChange,
                                        renderWidget }) {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(0);
  const [scale, setScale] = useState(1);

  // Measure the container itself rather than the window: collapsing the
  // Sidenav changes this width without firing a window resize.
  useLayoutEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;

    const measure = () => {
      setWidth(node.offsetWidth);
      setScale(getCssZoom() || 1);
    };
    measure();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // The zoom can also change without the container resizing — ZoomControls
  // sets it directly on <body>.
  useEffect(() => {
    const onResize = () => setScale(getCssZoom() || 1);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const layouts = useMemo(() => normaliseLayouts(layout, widgets), [layout, widgets]);

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        // RGL positions children absolutely; without this the placeholder and
        // the resize handle are clipped by the page's own overflow.
        '& .react-grid-item.react-grid-placeholder': {
          backgroundColor: 'rgba(21, 101, 200, 0.12)',
          borderRadius: '14px',
          border: '2px dashed rgba(21, 101, 200, 0.45)',
          opacity: 1,
        },
        '& .react-grid-item > .react-resizable-handle': {
          opacity: editable ? 0.35 : 0,
          transition: 'opacity .15s',
        },
        '& .react-grid-item:hover > .react-resizable-handle': {
          opacity: editable ? 0.9 : 0,
        },
      }}
    >
      {width > 0 && (
        <ResponsiveGridLayout
          width={width}
          layouts={layouts}
          breakpoints={BREAKPOINTS}
          cols={COLUMNS}
          rowHeight={ROW_HEIGHT}
          margin={MARGIN}
          containerPadding={[0, 0]}
          // Correct the pointer-to-layout mismatch that body zoom creates.
          transformScale={scale}
          isDraggable={editable}
          isResizable={editable}
          // Drag by the header only. Dragging from anywhere would swallow every
          // click inside a widget — including the drill-through link.
          draggableHandle=".widget-drag-handle"
          // A widget whose content scrolls must not start a drag when the user
          // scrolls it.
          draggableCancel=".widget-no-drag"
          useCSSTransforms
          measureBeforeMount={false}
          compactType="vertical"
          onLayoutChange={(current, all) => editable && onLayoutChange?.(all)}
        >
          {widgets.map((widget) => (
            <div key={widget.grid_key}>{renderWidget(widget)}</div>
          ))}
        </ResponsiveGridLayout>
      )}
    </Box>
  );
}

DashboardGrid.propTypes = {
  layout: PropTypes.object,
  widgets: PropTypes.array.isRequired,
  editable: PropTypes.bool,
  onLayoutChange: PropTypes.func,
  renderWidget: PropTypes.func.isRequired,
};

/**
 * Fill in any breakpoint or widget the stored layout does not cover.
 *
 * A widget added while the browser was wide has no entry at `sm`, and RGL
 * would stack it at the origin on top of another tile. Generating a position
 * for the missing ones keeps a dashboard usable on a narrower screen than the
 * one it was built on.
 */
function normaliseLayouts(stored, widgets) {
  const out = {};
  const keys = widgets.map((w) => w.grid_key);

  Object.keys(COLUMNS).forEach((breakpoint) => {
    const existing = Array.isArray(stored?.[breakpoint]) ? stored[breakpoint] : [];
    const byKey = new Map(existing.filter((i) => keys.includes(i.i)).map((i) => [i.i, i]));
    const columns = COLUMNS[breakpoint];

    let cursorX = 0;
    let cursorY = 0;
    let rowHeight = 0;

    out[breakpoint] = widgets.map((widget) => {
      const found = byKey.get(widget.grid_key);
      if (found) return { ...found, ...limits(widget) };

      const size = WIDGET_SIZES[widget.widget_kind] ?? WIDGET_SIZES.report_chart;
      const w = Math.min(size.w, columns);
      if (cursorX + w > columns) { cursorX = 0; cursorY += rowHeight; rowHeight = 0; }
      const item = {
        i: widget.grid_key, x: cursorX, y: cursorY, w, h: size.h, ...limits(widget),
      };
      cursorX += w;
      rowHeight = Math.max(rowHeight, size.h);
      return item;
    });
  });

  return out;
}

function limits(widget) {
  const size = WIDGET_SIZES[widget.widget_kind] ?? WIDGET_SIZES.report_chart;
  return { minW: size.minW, minH: size.minH };
}
